import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest, HttpEvent, HttpErrorResponse } from '@angular/common/http';

// RxJS
import {BehaviorSubject, Observable, throwError} from 'rxjs';

// Services
import { AuthService } from '../services/auth.service';
import {catchError, filter, switchMap, take} from 'rxjs/operators';

/**
 * Application auth token Interceptor
 *
 * @export
 * @class TokenInterceptor
 * @implements {HttpInterceptor}
 */
@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    /**
     * Creates an instance of TokenInterceptor.
     *
     * @param injector
     * @param authService
     * @memberof TokenInterceptor
     */
    constructor(private injector: Injector, private authService: AuthService) { }

    /**
     * Handles token insertion in requests to API and tokens renewal.
     *
     * If the incoming URL returns a 401 status code, this interceptor will make a request to renew the access token, if
     * none is already in progress.  If the renewal URL returns a new token, we update the stored access token and
     * proceed again with the initial request.  If the renewal fails, as it happens when the refresh token has expired
     * or has been revoked, the user is signed out and redirected to the login page.
     *
     * @memberof TokenInterceptor
     * @param req
     * @param next
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      let authReq = req;

      authReq = this.authService.addXAuthHeader(req);

      if (req.url.endsWith('/auth/refresh/')) {
          authReq = this.authService.addRefreshAuthorizationHeader(authReq);
      } else {
        authReq = this.authService.addAuthorizationHeader(authReq);
      }

      return next.handle(authReq).pipe(catchError(error => {
        if (
          error instanceof HttpErrorResponse
          && !authReq.url.includes('auth/login')
          && !authReq.url.includes('auth/refresh')
          && error.status === 401
        ) {
          return this.handle401Error(authReq, next);
        }

        return throwError(error);
      }));
    }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const token = this.authService.getRefreshToken();

      if (token) {
        return this.authService.refreshToken(token).pipe(
          switchMap((data: any) => {
            this.authService.storeToken(data.access_token);
            this.refreshTokenSubject.next(data.access_token);
            this.isRefreshing = false;

            return next.handle(this.authService.addAuthorizationHeader(request));
          }),
          catchError((err) => {
            this.isRefreshing = false;

            this.authService.logout();
            return throwError(err);
          }),
        );
      }
    }

    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap((token) => next.handle(this.authService.addAuthorizationHeader(request)))
    );
  }
}
